/**
 * Material Upload Modal
 *
 * Two-step modal for uploading learning materials:
 * 1. Select file via drag-and-drop
 * 2. Enter metadata and upload
 */

import { useState, useCallback } from 'react';
import { X, Upload, Video, FileText, Loader2, CheckCircle2, AlertCircle } from 'lucide-react';
import { useSopLookup } from '../../../hooks/useSOPs';
import {
  useCreateMaterial,
  useUploadFile,
  useConfirmUpload,
} from '../../../hooks/useLearning';

interface MaterialUploadModalProps {
  isOpen: boolean;
  onClose: () => void;
}

type Step = 'select' | 'metadata' | 'uploading' | 'success' | 'error';

interface FileData {
  file: File;
  type: 'video' | 'document';
  preview?: string;
}

export function MaterialUploadModal({ isOpen, onClose }: MaterialUploadModalProps) {
  const [step, setStep] = useState<Step>('select');
  const [fileData, setFileData] = useState<FileData | null>(null);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [sopId, setSopId] = useState<string>('');
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);

  const { data: sops } = useSopLookup();
  const createMaterial = useCreateMaterial();
  const uploadFile = useUploadFile();
  const confirmUpload = useConfirmUpload();

  const handleClose = () => {
    setStep('select');
    setFileData(null);
    setTitle('');
    setDescription('');
    setSopId('');
    setUploadProgress(0);
    setError(null);
    onClose();
  };

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file) handleFileSelect(file);
  }, []);

  const handleFileSelect = (file: File) => {
    const isVideo = file.type.startsWith('video/');
    const isDocument = file.type === 'application/pdf';

    if (!isVideo && !isDocument) {
      setError('Please select a video (MP4, MOV, WebM) or document (PDF) file.');
      return;
    }

    setFileData({
      file,
      type: isVideo ? 'video' : 'document',
    });
    setTitle(file.name.replace(/\.[^/.]+$/, '')); // Remove extension for default title
    setStep('metadata');
    setError(null);
  };

  const handleUpload = async () => {
    if (!fileData || !title.trim()) return;

    try {
      setStep('uploading');
      setError(null);

      // Step 1: Create material record and get presigned URL
      const result = await createMaterial.mutateAsync({
        title: title.trim(),
        description: description.trim() || undefined,
        materialType: fileData.type,
        sopId: sopId || undefined,
        fileName: fileData.file.name,
        contentType: fileData.file.type,
      });

      // Step 2: Upload file to S3
      await uploadFile.mutateAsync({
        url: result.upload.url,
        file: fileData.file,
        onProgress: setUploadProgress,
      });

      // Step 3: Confirm upload
      await confirmUpload.mutateAsync({
        id: result.material.id,
      });

      setStep('success');
    } catch (err) {
      console.error('Upload failed:', err);
      setError(err instanceof Error ? err.message : 'Upload failed. Please try again.');
      setStep('error');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="relative w-full max-w-lg rounded-2xl bg-white shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-gray-100 px-6 py-4">
          <h2 className="text-lg font-semibold text-gray-900">
            {step === 'success' ? 'Upload Complete' : 'Upload Material'}
          </h2>
          <button
            onClick={handleClose}
            className="rounded-lg p-2 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Step 1: File Selection */}
          {step === 'select' && (
            <div
              onDrop={handleDrop}
              onDragOver={(e) => e.preventDefault()}
              className="relative"
            >
              <label className="flex cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed border-gray-200 bg-gray-50/50 p-12 transition-colors hover:border-guild-green/50 hover:bg-guild-mint/10">
                <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-guild-green/10">
                  <Upload size={28} className="text-guild-green" />
                </div>
                <p className="mb-2 text-center text-sm font-medium text-gray-700">
                  Drag and drop your file here, or click to browse
                </p>
                <p className="text-center text-xs text-gray-500">
                  Supports MP4, MOV, WebM (video) or PDF (document)
                </p>
                <input
                  type="file"
                  className="hidden"
                  accept="video/mp4,video/quicktime,video/webm,application/pdf"
                  onChange={(e) => e.target.files?.[0] && handleFileSelect(e.target.files[0])}
                />
              </label>

              {error && (
                <div className="mt-4 flex items-center gap-2 rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700">
                  <AlertCircle size={16} />
                  {error}
                </div>
              )}
            </div>
          )}

          {/* Step 2: Metadata Entry */}
          {step === 'metadata' && fileData && (
            <div className="space-y-5">
              {/* Selected File */}
              <div className="flex items-center gap-4 rounded-xl bg-gray-50 p-4">
                <div
                  className={`flex h-12 w-12 items-center justify-center rounded-xl ${
                    fileData.type === 'video'
                      ? 'bg-violet-100 text-violet-600'
                      : 'bg-amber-100 text-amber-600'
                  }`}
                >
                  {fileData.type === 'video' ? <Video size={24} /> : <FileText size={24} />}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="truncate font-medium text-gray-900">{fileData.file.name}</p>
                  <p className="text-sm text-gray-500">
                    {(fileData.file.size / (1024 * 1024)).toFixed(2)} MB
                  </p>
                </div>
                <button
                  onClick={() => {
                    setFileData(null);
                    setStep('select');
                  }}
                  className="rounded-lg p-2 text-gray-400 hover:bg-gray-200 hover:text-gray-600"
                >
                  <X size={16} />
                </button>
              </div>

              {/* Title */}
              <div>
                <label className="mb-1.5 block text-sm font-medium text-gray-700">
                  Title <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Enter material title"
                  className="w-full rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm transition-colors focus:border-guild-green focus:outline-none focus:ring-2 focus:ring-guild-green/20"
                />
              </div>

              {/* Description */}
              <div>
                <label className="mb-1.5 block text-sm font-medium text-gray-700">
                  Description
                </label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Optional description"
                  rows={3}
                  className="w-full resize-none rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm transition-colors focus:border-guild-green focus:outline-none focus:ring-2 focus:ring-guild-green/20"
                />
              </div>

              {/* Linked SOP */}
              <div>
                <label className="mb-1.5 block text-sm font-medium text-gray-700">
                  Link to SOP (optional)
                </label>
                <select
                  value={sopId}
                  onChange={(e) => setSopId(e.target.value)}
                  className="w-full rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm transition-colors focus:border-guild-green focus:outline-none focus:ring-2 focus:ring-guild-green/20"
                >
                  <option value="">No linked SOP</option>
                  {sops?.map((sop) => (
                    <option key={sop.id} value={sop.id}>
                      {sop.title}
                    </option>
                  ))}
                </select>
              </div>

              {error && (
                <div className="flex items-center gap-2 rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700">
                  <AlertCircle size={16} />
                  {error}
                </div>
              )}
            </div>
          )}

          {/* Step 3: Uploading */}
          {step === 'uploading' && (
            <div className="flex flex-col items-center py-8">
              <div className="mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-guild-green/10">
                <Loader2 size={32} className="animate-spin text-guild-green" />
              </div>
              <p className="mb-4 text-sm font-medium text-gray-700">Uploading material...</p>
              <div className="w-full">
                <div className="mb-2 flex justify-between text-xs text-gray-500">
                  <span>Progress</span>
                  <span>{uploadProgress}%</span>
                </div>
                <div className="h-2 overflow-hidden rounded-full bg-gray-100">
                  <div
                    className="h-full rounded-full bg-gradient-to-r from-guild-green to-guild-mint transition-all duration-300"
                    style={{ width: `${uploadProgress}%` }}
                  />
                </div>
              </div>
            </div>
          )}

          {/* Step 4: Success */}
          {step === 'success' && (
            <div className="flex flex-col items-center py-8">
              <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-emerald-100">
                <CheckCircle2 size={32} className="text-emerald-600" />
              </div>
              <h3 className="mb-2 text-lg font-semibold text-gray-900">Upload Complete!</h3>
              <p className="text-center text-sm text-gray-500">
                Your material has been uploaded and is ready to assign to operators.
              </p>
            </div>
          )}

          {/* Step 5: Error */}
          {step === 'error' && (
            <div className="flex flex-col items-center py-8">
              <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-red-100">
                <AlertCircle size={32} className="text-red-600" />
              </div>
              <h3 className="mb-2 text-lg font-semibold text-gray-900">Upload Failed</h3>
              <p className="mb-4 text-center text-sm text-gray-500">{error}</p>
              <button
                onClick={() => setStep('metadata')}
                className="text-sm font-medium text-guild-green hover:underline"
              >
                Try again
              </button>
            </div>
          )}
        </div>

        {/* Footer */}
        {(step === 'metadata' || step === 'success') && (
          <div className="flex justify-end gap-3 border-t border-gray-100 px-6 py-4">
            {step === 'metadata' && (
              <>
                <button
                  onClick={handleClose}
                  className="rounded-xl border border-gray-200 px-5 py-2.5 text-sm font-semibold text-gray-700 transition-colors hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleUpload}
                  disabled={!title.trim()}
                  className="rounded-xl bg-guild-green px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-guild-green/90 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Upload Material
                </button>
              </>
            )}
            {step === 'success' && (
              <button
                onClick={handleClose}
                className="rounded-xl bg-guild-green px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-guild-green/90"
              >
                Done
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
